'use client';

import { type FC, useState } from 'react';
import {
  FiMapPin,
  FiBriefcase,
  FiMail,
  FiPhone,
  FiGlobe,
  FiLinkedin,
} from 'react-icons/fi';

import './PersonalLeadCard.styles.scss';

// Type matching the provided data shape
export type PersonalLead = {
  _id: string;
  email?: string;
  website?: string;
  last_name?: string;
  first_name?: string;
  company_name?: string;
  phone?: string;
  leadStatus?: string;
  linkedin_url?: string;
  title?: string;
  state?: string;
  city?: string;
  country?: string;
  keywords?: string;
  industry?: string;
  company_domain?: string;
  createdAt: string;
  [key: string]: unknown;
};

type PersonalLeadCardProps = {
  lead: PersonalLead;
  hasLink?: boolean;
  className?: string;
  onQuickPreview?: (lead: PersonalLead) => void;
  showFooter?: boolean;
};

function getStatusClass(status: string): string {
  const s = status.toLowerCase().replace('_', ' ');
  if (s === 'confirmed') return 'confirmed';
  if (s === 'pending' || s === 'in progress') return 'pending';
  if (s === 'rejected' || s === 'lost') return 'rejected';
  return 'default';
}

function getInitials(first?: string, last?: string): string {
  return [first?.[0], last?.[0]].filter(Boolean).join('').toUpperCase() || '?';
}

const PersonalLeadCard: FC<PersonalLeadCardProps> = ({
  lead,
  hasLink = true,
  className,
  onQuickPreview,
  showFooter = true,
}) => {
  const [showFullKeywords, setShowFullKeywords] = useState(false);

  const leadStatus =
    lead.leadStatus?.toLowerCase().replace('_', ' ') || 'no status';
  const statusClass = getStatusClass(lead.leadStatus || '');

  const fullName =
    `${lead.first_name || ''} ${lead.last_name || ''}`.trim() ||
    'No name provided';
  const location =
    [lead.city, lead.state, lead.country].filter(Boolean).join(', ') ||
    'Not found';

  const keywordsList = lead.keywords?.split(', ') || [];
  const displayedKeywords = showFullKeywords
    ? keywordsList
    : keywordsList.slice(0, 10);

  return (
    <div className={`personalLeadCard ${className || ''}`}>
      {/* ── Header ── */}
      <div className="cardHeader">
        <div className="headerLeft">
          <div className="nameRow">
            <div className="avatar">
              {getInitials(lead.first_name, lead.last_name)}
            </div>
            <div className="nameBlock">
              <div className="fullName">{fullName}</div>
              <div className="jobTitle">
                {lead.title || 'No title provided'}
              </div>
            </div>
          </div>
        </div>
        <div className="headerRight">
          <span className={`statusBadge ${statusClass}`}>
            <span className="statusDot" />
            {leadStatus}
          </span>
        </div>
      </div>

      {/* ── Company ── */}
      {(lead.company_name || lead.industry) && (
        <div className="companyRow">
          <div className="companyIcon">
            {lead.company_domain ? (
              <img
                src={`https://www.google.com/s2/favicons?domain=${lead.company_domain}&sz=32`}
                alt={`${lead.company_name || 'Company'} icon`}
              />
            ) : (
              <FiBriefcase className="companyIconFallback" />
            )}
          </div>
          {lead.company_name && (
            <span className="companyName">{lead.company_name}</span>
          )}
          {lead.industry && (
            <span className="industryTag">{lead.industry}</span>
          )}
        </div>
      )}

      <div className="divider" />

      {/* ── Details ── */}
      <div className="leadDetails">
        <div className="detail">
          <FiMapPin className="icon" />
          <span>{location}</span>
        </div>

        {lead.email && (
          <div className="detail">
            <FiMail className="icon" />
            <a href={`mailto:${lead.email}`}>{lead.email}</a>
          </div>
        )}

        {lead.phone && (
          <div className="detail">
            <FiPhone className="icon" />
            <span>{lead.phone}</span>
          </div>
        )}

        {lead.website && (
          <div className="detail">
            <FiGlobe className="icon" />
            <a href={lead.website} target="_blank" rel="noopener noreferrer">
              {lead.website.replace(/^https?:\/\//, '')}
            </a>
          </div>
        )}

        {lead.linkedin_url && (
          <div className="detail">
            <FiLinkedin className="icon" />
            <a
              href={lead.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              LinkedIn Profile
            </a>
          </div>
        )}
      </div>

      {/* ── Keywords ── */}
      {lead.keywords && (
        <>
          <div className="divider" />
          <div className="keywordsSection">
            <div className="sectionLabel">Keywords</div>
            <div className="keywords">
              {displayedKeywords.map((keyword, index) => (
                <span key={index} className="keyword">
                  {keyword.trim()}
                </span>
              ))}
              {keywordsList.length > 10 && (
                <button
                  className="showMoreBtn"
                  onClick={() => setShowFullKeywords(!showFullKeywords)}
                >
                  {showFullKeywords
                    ? 'Show less'
                    : `+${keywordsList.length - 10} more`}
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Footer ── */}
      {showFooter && (
        <div className="cardFooter">
          {hasLink && (
            <span className="createdAt">
              Created{' '}
              {new Date(lead.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          )}
          {hasLink && (
            <div className="actions">
              <button
                className="previewBtn"
                onClick={() => onQuickPreview?.(lead)}
              >
                Quick preview
              </button>
              <a
                href={`/lead/${lead._id}?campaignType=personal`}
                className="fullPageLink"
              >
                Full page
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PersonalLeadCard;
